/**
 * CalendarDayOverflow — the "+N more" affordance for a day cell that has more
 * events than fit its fixed height (BON-142). Hovering/focusing the trigger
 * shows a tooltip previewing the hidden events; clicking opens a small
 * popover listing them as selectable pills (so a busy day never hides a
 * workshop the CSR can't reach) — outside-click or Escape closes it.
 *
 * Deliberately NOT inside the day cell's own `overflow: hidden` events area
 * (see `WorkshopCalendarPicker`'s `DayCell`) so the popover isn't clipped.
 */
import { useEffect, useRef, useState } from 'react';

import { Tooltip } from '../Tooltip';

import type { CalendarEvent } from './calendarEvent';
import { CalendarEventPill } from './CalendarEventPill';

function OverflowPreview({ events }: { events: CalendarEvent[] }) {
  return (
    <div className="space-y-1.5">
      {events.map((e) => (
        <div
          key={e.id}
          className="border-b border-stone-100 pb-1 last:border-0 last:pb-0 dark:border-stone-700"
        >
          <p className="font-semibold text-bark-900 dark:text-stone-100">
            {e.tooltip?.title ?? e.title}
          </p>
          {e.tooltip && (
            <p>
              {e.tooltip.venueName} &middot; {e.tooltip.dateLabel} &middot; {e.tooltip.timeLabel}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

/** Close the popover on an outside click or Escape while it's open. */
function useCloseOnOutsideOrEscape(
  open: boolean,
  onClose: () => void,
  ref: React.RefObject<HTMLElement>,
) {
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, ref]);
}

export function CalendarDayOverflow({
  hidden,
  selectedId,
  onSelect,
}: {
  hidden: CalendarEvent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useCloseOnOutsideOrEscape(open, () => setOpen(false), containerRef);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Tooltip content={<OverflowPreview events={hidden} />} className="block w-full">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 600,
            color: 'var(--nb-primary, #6b8e5a)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          +{hidden.length} more
        </button>
      </Tooltip>
      {open && (
        <div
          role="listbox"
          aria-label="More workshops"
          className="absolute left-0 top-full z-[70] mt-1 max-h-56 w-64 overflow-y-auto rounded-lg border border-stone-200 bg-white p-1.5 shadow-lg dark:border-stone-700 dark:bg-[#1e2520]"
        >
          {hidden.map((e) => (
            <CalendarEventPill
              key={e.id}
              event={e}
              selected={selectedId === e.id}
              onSelect={(id) => {
                onSelect(id);
                setOpen(false);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
